import { Canvas } from "@faro-lotv/app-component-toolbox";
import { SurfaceWandProvider } from "@/tools/surface-wand/surface-wand-context";
import { PerformanceMonitor } from "@react-three/drei";
import { Dpr } from "@react-three/fiber";
import { ComponentProps, forwardRef, useEffect, type JSX } from "react";
import { create } from "zustand";
import { ExposeThree } from "../utils/expose-three";

type AdaptiveResolutionCanvasProps = Omit<ComponentProps<typeof Canvas>, "dpr">;

/** R3F's own default: clamp(devicePixelRatio, 1, 2) */
const DEFAULT_DPR: Dpr = [1, 2];

type CanvasDprStore = { dpr: Dpr; setDpr(dpr: Dpr): void };

const useCanvasDprStore = create<CanvasDprStore>((set) => ({
  dpr: DEFAULT_DPR,
  setDpr: (dpr) => set({ dpr }),
}));

/** Some scenes (FlyWalk, Overview) clamps the maximum dpr to improve rendering performance */
const MAX_DPR = 1.5;
const CLAMPED_DPR: Dpr = [1, MAX_DPR];

/**
 * Temporarily clamp the canvas DPR to [1, 1.5] while this component is mounted and restore the
 * previous range on unmount.
 */
export function useReduceDprRange(): void {
  useEffect(() => {
    const { dpr: previous, setDpr } = useCanvasDprStore.getState();
    setDpr(CLAMPED_DPR);
    return () => setDpr(previous);
  }, []);
}

/**
 * @returns A canvas whose dpr is automatically adjusted based on the screen resolution.
 * Renders at a DPR between 1 and 2 by default, but some views temporarily narrow that range to
 * keep GPU work and memory down when the scene is expensive. This improves responsiveness in heavy
 * scenes, at the cost of slightly softer output. DPR stays above 1.5 only in simpler, high-density
 * cases where sharper pixels matter more than the extra render cost.
 */
export const AdaptiveResolutionCanvas = forwardRef<
  HTMLCanvasElement,
  AdaptiveResolutionCanvasProps
>(function AdaptiveResolutionCanvas(
  { children, ...props }: AdaptiveResolutionCanvasProps,
  ref,
): JSX.Element {
  const dpr = useCanvasDprStore((s) => s.dpr);
  return (
    <Canvas ref={ref} {...props} dpr={dpr}>
      {/* Make the Three JS scene available to Playwright for more reliable E2E testing */}
      <ExposeThree />

      <SurfaceWandProvider>
        <PerformanceMonitor>{children}</PerformanceMonitor>
      </SurfaceWandProvider>
    </Canvas>
  );
});
